import { useT } from "@magic-md/i18n/react";
import {
    Activity,
    Bug,
    Eye,
    GanttChart,
    List,
    Maximize2,
    Minimize2,
    PanelBottomClose,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { useLogStore } from "../../store/log-store";
import { ensureOutputWatchNotificationHandlers } from "../../store/output-watch-store";
import { ensureTraceNotificationHandlers } from "../../store/trace-store";
import { ExecutionTimeline } from "../ExecutionTimeline";
import { DebugPanel } from "./DebugPanel";
import { LogEntryRow } from "./LogEntryRow";
import { LogFilterBar } from "./LogFilterBar";
import { OutputWatchPanel } from "./OutputWatchPanel";
import { TracePanel } from "./TracePanel";

const MIN_PANEL_HEIGHT = 100;
const DEFAULT_PANEL_HEIGHT = 240;
const DEFAULT_PANEL_HEIGHT_RATIO = 0.3;

function getMaxPanelHeight(): number {
    if (typeof window === "undefined") return Number.POSITIVE_INFINITY;
    return Math.max(MIN_PANEL_HEIGHT, window.innerHeight - 8);
}

function getDefaultPanelHeight(): number {
    if (typeof window === "undefined") return DEFAULT_PANEL_HEIGHT;
    return Math.min(
        getMaxPanelHeight(),
        Math.max(DEFAULT_PANEL_HEIGHT, Math.round(window.innerHeight * DEFAULT_PANEL_HEIGHT_RATIO)),
    );
}

export function LogPanel() {
    const t = useT("editor");
    const isOpen = useLogStore((s) => s.isOpen);
    const getFilteredEntries = useLogStore((s) => s.getFilteredEntries);
    const entries = useLogStore((s) => s.entries);
    const activeTab = useLogStore((s) => s.activeTab);
    const setActiveTab = useLogStore((s) => s.setActiveTab);
    const closePanel = useLogStore((s) => s.closePanel);
    const [panelHeight, setPanelHeight] = useState(() => getDefaultPanelHeight());
    const [maximized, setMaximized] = useState(false);
    const draggingRef = useRef(false);
    const startYRef = useRef(0);
    const startHeightRef = useRef(0);

    const filtered = getFilteredEntries();

    const scrollRef = useRef<HTMLDivElement>(null);
    const shouldAutoScroll = useRef(true);

    useEffect(() => {
        ensureOutputWatchNotificationHandlers();
        ensureTraceNotificationHandlers();
    });

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        shouldAutoScroll.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    }, []);

    useEffect(() => {
        if (shouldAutoScroll.current && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    });

    useEffect(() => {
        const onWindowResize = () => {
            setPanelHeight((height) => Math.min(getMaxPanelHeight(), Math.max(MIN_PANEL_HEIGHT, height)));
        };
        window.addEventListener("resize", onWindowResize);
        return () => window.removeEventListener("resize", onWindowResize);
    }, []);

    if (!isOpen) return null;

    const onResizeMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        draggingRef.current = true;
        startYRef.current = e.clientY;
        startHeightRef.current = panelHeight;

        const onMouseMove = (ev: MouseEvent) => {
            if (!draggingRef.current) return;
            const delta = startYRef.current - ev.clientY;
            setPanelHeight(
                Math.max(MIN_PANEL_HEIGHT, Math.min(getMaxPanelHeight(), startHeightRef.current + delta)),
            );
        };
        const onMouseUp = () => {
            draggingRef.current = false;
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "row-resize";
        document.body.style.userSelect = "none";
    };

    return (
        <div
            className="relative z-30 flex w-full shrink-0 flex-col border-t border-border bg-background"
            style={{ height: maximized ? "100%" : panelHeight }}
        >
            {/* Vertical resize handle */}
            <button
                type="button"
                className="h-1 cursor-row-resize bg-transparent hover:bg-primary/20 active:bg-primary/30 transition-colors shrink-0"
                onMouseDown={onResizeMouseDown}
                onKeyDown={(e) => {
                    if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setPanelHeight((h) => Math.min(getMaxPanelHeight(), h + 12));
                    } else if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setPanelHeight((h) => Math.max(MIN_PANEL_HEIGHT, h - 12));
                    }
                }}
                aria-label={t("log.resizePanel")}
                tabIndex={0}
            />

            {/* Tab bar */}
            <div className="flex h-8 shrink-0 items-end border-b border-border bg-muted/10">
                <div
                    className="flex h-full min-w-0 flex-1 flex-nowrap items-end gap-1 overflow-x-auto overflow-y-hidden px-2 pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    role="tablist"
                    aria-label={t("log.panelViews")}
                >
                    {(
                        [
                            { id: "logs", icon: List, label: t("log.tab.logs") },
                            { id: "timeline", icon: GanttChart, label: t("log.tab.timeline") },
                            { id: "trace", icon: Activity, label: t("log.tab.trace") },
                            { id: "debug", icon: Bug, label: t("log.tab.debug") },
                            { id: "watch", icon: Eye, label: t("log.tab.watches") },
                        ] as const
                    ).map(({ id, icon: Icon, label }) => (
                        <button
                            key={id}
                            type="button"
                            className={cn(
                                "flex h-7 shrink-0 items-center gap-1 rounded-t-md border border-b-0 px-2.5 text-[10px] font-medium transition-colors",
                                activeTab === id
                                    ? "border-border bg-background text-foreground shadow-[0_-1px_0_var(--border)_inset]"
                                    : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                            )}
                            onClick={() => setActiveTab(id)}
                            role="tab"
                            aria-selected={activeTab === id}
                        >
                            <Icon size={12} />
                            {label}
                        </button>
                    ))}
                </div>

                <div className="mb-1 mr-2 flex h-7 shrink-0 items-center">
                    <button
                        type="button"
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        onClick={() => setMaximized((m) => !m)}
                        aria-label={maximized ? t("log.restoreHeight") : t("log.maximizeHeight")}
                        title={maximized ? t("log.restoreHeight") : t("log.maximizeHeight")}
                    >
                        {maximized ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                    </button>
                    <button
                        type="button"
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        onClick={closePanel}
                        aria-label={t("log.collapsePanel")}
                        title={t("log.collapsePanel")}
                    >
                        <PanelBottomClose size={12} />
                    </button>
                </div>
            </div>

            {/* Tab content */}
            {activeTab === "logs" ? (
                <>
                    <LogFilterBar />
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-auto"
                        onScroll={handleScroll}
                    >
                        {filtered.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-[11px] text-muted-foreground/40">
                                No log entries
                            </div>
                        ) : (
                            filtered.map((entry) => (
                                <LogEntryRow key={entry.id} entry={entry} />
                            ))
                        )}
                    </div>
                    <div className="flex items-center px-3 py-0.5 border-t border-border/50 bg-muted/10 text-[10px] text-muted-foreground/50">
                        <span>
                            {filtered.length} / {entries.length} entries
                        </span>
                    </div>
                </>
            ) : activeTab === "timeline" ? (
                <ExecutionTimeline />
            ) : activeTab === "trace" ? (
                <TracePanel />
            ) : activeTab === "debug" ? (
                <DebugPanel />
            ) : (
                <OutputWatchPanel />
            )}
        </div>
    );
}
